import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { ContainedAssetCard } from '@cbhq/cds-web/cards/ContainedAssetCard';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3, TextTitle4 } from '@cbhq/cds-web/typography';

import { ReactComponent as CoinbaseWalletApiImg } from ':cloud/assets/Products/CoinbaseWalletApi.svg';
import { ReactComponent as CoinbaseWalletApiImgDark } from ':cloud/assets/Products/CoinbaseWalletApiDark.svg';
import { ReactComponent as EthClientDiversityImg } from ':cloud/assets/Products/EthClientDiversity.svg';
import { ReactComponent as EthClientDiversityImgDark } from ':cloud/assets/Products/EthClientDiversityDark.svg';
import { ReactComponent as StakingHeaderImg } from ':cloud/assets/Products/StakingHeaderImg.svg';
import { ReactComponent as StakingHeaderImgDark } from ':cloud/assets/Products/StakingHeaderImgDark.svg';
import { ReactComponent as UptimeSecurityImg } from ':cloud/assets/Products/UptimeSecurity.svg';
import { ReactComponent as UptimeSecurityImgDark } from ':cloud/assets/Products/UptimeSecurityDark.svg';
import { useAppState } from ':cloud/contexts/AppStateContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import {
  CB_WALLET,
  CLOUD_SALES,
  ETH_DIVERSITY,
  STAKING_DISCORD_LINK,
  STAKING_DOCS_LINK,
  UPTIME_SECURITY_LINK,
} from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { AppRoute } from ':cloud/utils/routes';
import { Layout } from ':cloud/widgets/layout';
import { FULL_PAGE_MAX_WIDTH, MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { ExampleSection, ExampleSectionItem } from ':cloud/widgets/sharedcomponents/ExampleSection';
import {
  KeyFeatureItem,
  KeyFeaturesSection,
} from ':cloud/widgets/sharedcomponents/KeyFeaturesSection';
import SimpleCarousel from ':cloud/widgets/sharedcomponents/SimpleCarousel';
import CodeExampleSection from ':cloud/widgets/staking/CodeExampleSection';
import networkData from ':cloud/widgets/staking/networkData';
import StakingSolutions from ':cloud/widgets/staking/StakingSolutions';

const keyFeatures: KeyFeatureItem[] = [
  {
    iconName: 'gasFees',
    title: 'Integrate quickly',
    description:
      'Get up and running in a few minutes with self-service onboarding and quality docs. Build in Typescript, Golang, and Ruby.',
    iconType: 'pictogram',
  },
  {
    iconName: 'browserTransaction',
    title: 'View detailed reporting',
    description:
      'Access operation-agnostic historical and pending rewards data across ETH and SOL onchain addresses.',
    iconType: 'pictogram',
  },
  {
    iconName: 'security',
    title: 'Scale your operation',
    description:
      "Reach a fast-growing audience with a proven toolkit — through the use of our own APIs, we've built products serving millions of users.",
    iconType: 'pictogram',
  },
];

// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
const imageStyle = { maxWidth: '100%', height: 'auto', width: '100%' };

const learnItems: ExampleSectionItem[] = [
  {
    title: 'Learn how we support ETH Client Diversity',
    description:
      "We diversified ETH execution clients for enhanced security of our customers' staked assets.",
    cta: 'Read more',
    onPress: onExternalLinkPress(ETH_DIVERSITY),
    image: <EthClientDiversityImg style={imageStyle} />,
    darkImage: <EthClientDiversityImgDark style={imageStyle} />,
  },
  {
    title: 'See how Coinbase Wallet uses our API',
    description:
      'Coinbase Wallet enables users to stake ETH without minimum requirements through Staking API.',
    cta: 'Read more',
    onPress: onExternalLinkPress(CB_WALLET),
    image: <CoinbaseWalletApiImg style={imageStyle} />,
    darkImage: <CoinbaseWalletApiImgDark style={imageStyle} />,
  },
  {
    title: 'Learn how we balance uptime and security',
    description:
      'The philosophy and security of a 99% uptime guarantee, and why you should be wary of any uptime guarantee greater than 99%.',
    cta: 'Read more',
    onPress: onExternalLinkPress(UPTIME_SECURITY_LINK),
    image: <UptimeSecurityImg style={imageStyle} />,
    darkImage: <UptimeSecurityImgDark style={imageStyle} />,
  },
];

export function StakingAPILanding() {
  const { isDesktop } = useSimpleBreakpoints();
  const { isDarkMode } = useAppState();
  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '4fr 3fr' : '1fr';
  }, [isDesktop]);
  const history = useHistory();

  return (
    <Layout enableMaxWidth>
      <Layout.MainContainer
        minWidth={MAIN_CONTAINER_MIN_WIDTH}
        spacingTop={isDesktop ? 0 : 7}
        spacingHorizontal={0}
        fullWidth
      >
        <VStack>
          <Grid
            maxWidth={FULL_PAGE_MAX_WIDTH}
            templateColumns={responsiveTemplateColumns}
            gap={9}
            spacingHorizontal={8}
            borderedBottom
          >
            <VStack justifyContent="center">
              <TextDisplay3 as="h1">Enable staking for your users</TextDisplay3>
              <TextTitle4 as="h2" color="foregroundMuted" spacingTop={1} spacingBottom={7}>
                Use our API to build staking experiences for your users with just three lines of
                code. View our docs to start building in Typescript, Golang, and Ruby.
              </TextTitle4>

              <HStack flexWrap="wrap" gap={1}>
                <Button
                  variant="primary"
                  endIcon="diagonalUpArrow"
                  onPress={onExternalLinkPress(STAKING_DOCS_LINK)}
                  accessibilityLabel="View docs + opens in new window"
                >
                  View docs
                </Button>
                <Button
                  variant="secondary"
                  endIcon="diagonalUpArrow"
                  onPress={onExternalLinkPress(STAKING_DISCORD_LINK)}
                  accessibilityLabel="Discord channel + opens in new window"
                >
                  Discord channel
                </Button>
                <Button
                  variant="secondary"
                  onPress={onExternalLinkPress(CLOUD_SALES)}
                  accessibilityLabel="Discord channel + opens in new window"
                >
                  Contact sales
                </Button>
              </HStack>
            </VStack>
            <Box spacing={isDesktop ? 10 : 2} spacingBottom={isDesktop ? 10 : 7}>
              {isDarkMode ? (
                <StakingHeaderImgDark width="100%" />
              ) : (
                <StakingHeaderImg width="100%" />
              )}
            </Box>
          </Grid>
          <KeyFeaturesSection sectionTitle="Why choose our Staking API" features={keyFeatures} />
          <SimpleCarousel
            title="Networks"
            items={networkData.map((item) => {
              const { Logo } = item;
              return (
                <ContainedAssetCard
                  onPress={() => history.push(AppRoute.Products.StakingCommunity)}
                  title={item.networkName || item.network}
                  subtitle={item.hasApi && 'API enabled'}
                  header={<Logo height={32} width={32} />}
                />
              );
            })}
          />
          <CodeExampleSection />
          <StakingSolutions />
          <ExampleSection sectionTitle="Learn" exampleItems={learnItems} />
        </VStack>
      </Layout.MainContainer>
    </Layout>
  );
}
